import {Injectable} from '@angular/core';
import {
    BehaviorSubject,
    distinctUntilChanged,
    finalize,
    map,
    Observable,
    shareReplay,
    startWith,
    take,
    timer
} from "rxjs";
import {HttpClient, HttpParams} from "@angular/common/http";
import {environment} from '../../environments/environment';
import {CustomButtonToggleOption, GenericResponse, GenericResponseWithPagination} from '../_types/shared';
import {fetchAllPages} from '../_utils/http-params';
import {
    CommunityStatistic,
    FeedPayload,
    FeedPost,
    NewPost,
    Post,
    PostAttachmentType,
    PostComment,
    PostCommentLikeResponse,
    PostLikeResponse,
    PostNewComment,
    UpdatePost,
    UserStats
} from '../_types/content';
import {jsonToFormData} from '../_utils/form-data-builder';
import {Community, UserAccessLevel} from '../_types/communities';
import {LocalStorageService} from './local-storage.service';
import {ThemeService} from "./theme.service";
import {removeNullUndefinedFields} from "../_utils/remove-null-fields";
import {RewardsService} from "./rewards.service";

@Injectable({
    providedIn: 'root'
})
export class ContentService {

    private currentDashboardCommunity$: BehaviorSubject<Community | null> = new BehaviorSubject<Community | null>(null);
    private cacheCommunityStats$: Map<string, Observable<CommunityStatistic>> = new Map();
    private cacheUserStats$: Map<string, Observable<UserStats>> = new Map();


    constructor(private http: HttpClient, private localStorageService: LocalStorageService, private themeService: ThemeService, private rewardService: RewardsService
    ) {
        this.currentDashboardCommunity$.next(this.localStorageService.getItem<Community | null>('lastSelectedCommunity', null));
    }


    getCommunityPages(id: number): Observable<any> {
        return this.http.get<any>(`${environment.apiUrl}/communities/${id}/pages/`);
    }

    getCommunityPage(communityId: number, pageId: number): Observable<any> {
        return this.http.get<GenericResponse<any>>(`${environment.apiUrl}/communities/${communityId}/pages/${pageId}`).pipe(map(res => res.data));
    }

    getCurrentDashboardCommunity(): Observable<Community | null> {
        return this.currentDashboardCommunity$
            .pipe(
                startWith(this.localStorageService.getItem<Community | null>('lastSelectedCommunity', null)),
                distinctUntilChanged(this.customComparator)
            );
    }

    userHasCommunityRole(roles: UserAccessLevel[]): boolean {
        const community: Community | null = this.currentDashboardCommunity$.value;
        return !!community?.user_membership && roles.includes(community?.user_membership?.access_level);
    }

    setCurrentDashboardCommunity(community: Community | null): void {

        this.rewardService.cacheCurrentUserStreaks$ = null;
        this.rewardService.cacheUserStreaks$.clear();
        this.rewardService.cacheStreaks$ = null;

        this.localStorageService.setItem(community, 'lastSelectedCommunity')

        if (community?.main_color_hex) {
            this.themeService.savePrimaryColor(community?.main_color_hex)
        } else {
            this.themeService.savePrimaryColor(environment.primaryColor || '#1B9AAA')
        }

        if (community?.accent_color_hex) {
            this.themeService.saveAccentColor(community.accent_color_hex);
        } else {
            this.themeService.saveAccentColor(environment.accentColor || '#c93457');
        }

        if (community?.logo_background_color_hex) {
            document.documentElement.style.setProperty(
                `--logo-background`, community?.logo_background_color_hex
            );
        } else {
            document.documentElement.style.setProperty(
                `--logo-background`, 'transparent'
            );
        }

        if (community?.main_background_color_hex) {
            document.documentElement.style.setProperty(
                `--body-background-colour`, community?.main_background_color_hex
            );
        }

        if (community?.notification_badge_color_hex) {
            document.documentElement.style.setProperty(
                `--badge-background-color`, community?.notification_badge_color_hex
            );
        } else {
            document.documentElement.style.setProperty(
                `--badge-background-color`, 'var(--theme-accent-500)'
            );
        }

        if (community?.audio_background_color) {
            document.documentElement.style.setProperty(
                `--audio-background-color`, community?.audio_background_color
            );
        } else {
            document.documentElement.style.setProperty(
                `--audio-background-color`, 'var(--primary)'
            );
        }
        if (community?.audio_foreground_color) {
            document.documentElement.style.setProperty(
                `--audio-foreground-color`, community?.audio_foreground_color
            );
        } else {
            document.documentElement.style.setProperty(
                `--audio-foreground-color`, 'var(--accent)'
            );
        }

        if (community?.body_font_type?.name) {
            document.documentElement.style.setProperty(
                `--font-family`, "'" + community?.body_font_type?.name + "'"
            );
        } else {
            document.documentElement.style.setProperty(
                `--font-family`, 'roboto'
            );
        }

        if (community?.heading_font_type?.name) {
            document.documentElement.style.setProperty(
                `--heading-font-family`, "'" + community?.heading_font_type?.name + "'"
            );
        } else {
            document.documentElement.style.setProperty(
                `--heading-font-family`, 'roboto'
            );
        }


        if (community?.body_font_size) {
            document.documentElement.style.setProperty(
                `--body-font-size`, JSON.stringify(community?.body_font_size) + 'em'
            );
        } else {
            document.documentElement.style.setProperty(
                `--body-font-size`, '1em'
            );
        }

        if (community?.poll_vote_gradient_start_color_hex) {
            document.documentElement.style.setProperty(
                `--poll-vote-gradient-start-color-hex`, community?.poll_vote_gradient_start_color_hex
            );
        } else {
            document.documentElement.style.setProperty(
                `--poll-vote-gradient-start-color-hex`, '#003247'
            );
        }

        if (community?.poll_vote_gradient_end_color_hex) {
            document.documentElement.style.setProperty(
                `--poll-vote-gradient-end-color-hex`, community?.poll_vote_gradient_end_color_hex
            );
        } else {
            document.documentElement.style.setProperty(
                `--poll-vote-gradient-end-color-hex`, '#931772'
            );
        }

        if (community?.registration_progress_highlight_color_hex) {
            document.documentElement.style.setProperty(
                `--mat-stepper-header-selected-state-icon-background-color`, community?.registration_progress_highlight_color_hex
            );
            document.documentElement.style.setProperty(
                `--mat-stepper-header-selected-state-icon-foreground-color`, this.themeService.getReadableColor(community?.registration_progress_highlight_color_hex)
            );
        } else {
            document.documentElement.style.setProperty(
                `--mat-stepper-header-selected-state-icon-background-color`, environment.primaryColor || '#1B9AAA'
            );
            document.documentElement.style.setProperty(
                `--mat-stepper-header-selected-state-icon-foreground-color`, this.themeService.getReadableColor(environment.primaryColor || '#1B9AAA')
            );
        }


        if (community?.button_radius) {
            document.documentElement.style.setProperty(
                `--mdc-filled-button-container-shape`, JSON.stringify(community?.button_radius) + 'px'
            );
            document.documentElement.style.setProperty(
                `--mat-standard-button-toggle-shape`, JSON.stringify(community?.button_radius) + 'px'
            );
            document.documentElement.style.setProperty(
                `--mdc-protected-button-container-shape`, JSON.stringify(community?.button_radius) + 'px'
            );
        } else {
            document.documentElement.style.setProperty(
                `--mdc-filled-button-container-shape`, '5px'
            );
            document.documentElement.style.setProperty(
                `--mat-standard-button-toggle-shape`, '5px'
            );
            document.documentElement.style.setProperty(
                `--mdc-protected-button-container-shape`, '5px'
            );
        }


        if (community?.recent_activity_highlight_color_hex) {
            document.documentElement.style.setProperty(
                `--recent-activity-highlight-colour`, community?.recent_activity_highlight_color_hex
            );
        } else {
            document.documentElement.style.setProperty(
                `--recent-activity-highlight-colour`, 'var(--primary)'
            );
        }
        const favicon: HTMLElement | null = document.getElementById("favicon");
        if (community?.favicon && favicon) {
            favicon?.setAttribute("href", community.favicon);
        } else if (community?.logo && favicon) {
            favicon?.setAttribute("href", community.logo);
        } else if (favicon) {
            favicon?.setAttribute("href", 'assets/explainLogoPlaceholder.png');
        }
        if (community?.default_page_is_feed) {
            this.localStorageService.setItem(true, 'homeIsFeed')
        } else {
            this.localStorageService.removeItem('homeIsFeed')
        }

        this.currentDashboardCommunity$.next(community);
        this.themeService.updateCurrentBrightnessTheme();
    }


    searchGifs(query: string, limit: number, offset?: number): Observable<any> {
        let parameters: HttpParams = new HttpParams();
        parameters = parameters.set('api_key', 'zsidsAPTg0trCoXmjc0NtEKJmTml3Zff');
        parameters = parameters.set('q', query);
        parameters = parameters.set('rating', 'g');
        parameters = parameters.set('limit', limit);
        if (offset) {
            parameters = parameters.set('offset', offset * limit);
        }
        return this.http.get<any>(`https://api.giphy.com/v1/gifs/search`, {
            params: parameters
        });
    }

    getPosts(page: number, perPage = 10, filters: {
        is_pinned_to_sidebar?: boolean,
        community?: number,
    } = {}): Observable<GenericResponseWithPagination<Post>> {
        const payload = {
            ...filters,
            page: page,
            page_size: perPage,
        }
        return this.http.get<GenericResponseWithPagination<Post>>(`${environment.apiUrl}/posts/`, {params: payload});
    }

    getFeed(communityId: number, page: number, perPage = 10, filters: FeedPayload = {}): Observable<GenericResponseWithPagination<FeedPost>> {
        const payload = {
            ...filters,
            page: page,
            page_size: perPage,
        }
        return this.http.get<GenericResponseWithPagination<FeedPost>>(`${environment.apiUrl}/${communityId}/feed/`, {params: payload});
    }


    userStats(communityId: number, user_id: number, end_date?: string, start_date?: string): Observable<UserStats> {
        const id = `${communityId}_${start_date}_${end_date}`;
        if (!this.cacheUserStats$.has(id) && user_id) {
            let params = new HttpParams().set('community_id', communityId).append('user_id', user_id);
            if (start_date) {
                params = params.append('start_date', start_date);
            }
            if (end_date) {
                params = params.append('end_date', end_date);
            }
            const userStats$ = this.http.get<GenericResponse<UserStats>>(`${environment.apiUrl}/activity/user-stats`, {params}).pipe(
                finalize(() => timer(1000).pipe(take(1)).subscribe(() => this.cacheUserStats$.delete(id))),
                shareReplay({bufferSize: 1, refCount: true, windowTime: 1000}),
                map(response => response.data)
            );
            this.cacheUserStats$.set(id, userStats$);
        }
        return this.cacheUserStats$.get(id)!;
    }


    getRecentActivity(communityId: number, limit: number): Observable<any> {
        let parameters: HttpParams = new HttpParams();
        if (communityId) {
            parameters = parameters.set('community', communityId);
        }
        if (limit) {
            parameters = parameters.set('limit', limit);
        }
        return this.http.get<any>(`${environment.apiUrl}/activity/recent`, {params: parameters});
    }


    getCommunityStatistic(communityId: number, end_date?: string, start_date?: string): Observable<CommunityStatistic> {
        const id = `${communityId}_${start_date}_${end_date}`;
        if (!this.cacheCommunityStats$.has(id)) {
            let params = new HttpParams().set('community_id', communityId);
            if (start_date) {
                params = params.append('start_date', start_date);
            }
            if (end_date) {
                params = params.append('end_date', end_date);
            }
            const stats$ = this.http.get<GenericResponse<CommunityStatistic>>(`${environment.apiUrl}/activity/community-stats`, {params}).pipe(
                finalize(() => timer(1000).pipe(take(1)).subscribe(() => this.cacheCommunityStats$.delete(id))),
                shareReplay({bufferSize: 1, refCount: true, windowTime: 1000}),
                map(response => response.data)
            );
            this.cacheCommunityStats$.set(id, stats$);
        }
        return this.cacheCommunityStats$.get(id)!;
    }

    newPost(postData: NewPost): Observable<Post> {
        let formData: any;
        let files = postData.attachment_files
        delete postData['attachment_files'];
        formData = postData;
        if (formData.subgroups) {
            formData.subgroups = JSON.stringify(formData.subgroups).replace(/[\[\]']+/g, '');
        }
        if (formData.categories) {
            formData.categories = JSON.stringify(formData.categories).replace(/[\[\]']+/g, '');
        }
        formData = jsonToFormData(postData);
        if (files) {
            for (let file of files) {
                formData.append('attachment_files', file)
            }
        }
        return this.http.post<Post>(`${environment.apiUrl}/posts/`, formData);
    }

    updatePost(postId: number, postData: UpdatePost, notFormData?: boolean, queryParams?: any): Observable<Post> {
        let formData: any;
        let parameters: HttpParams = new HttpParams();
        if (queryParams?.remove_subgroups) {
            parameters = parameters.set('remove_subgroups', 1);
        }
        if (queryParams?.unlock_post) {
            parameters = parameters.set('unlock_post', 1);
        }

        if (notFormData) {
            return this.http.patch<Post>(`${environment.apiUrl}/posts/${postId}/`, postData, {params: parameters});
        } else {

            let files = postData.attachment_files
            delete postData['attachment_files'];
            formData = postData;
            if (formData.subgroups) {
                formData.subgroups = JSON.stringify(formData.subgroups).replace(/[\[\]']+/g, '');
            }
            if (formData.categories) {
                formData.categories = JSON.stringify(formData.categories).replace(/[\[\]']+/g, '');
            }

            formData = jsonToFormData(postData);
            if (files) {
                for (let file of files) {
                    formData.append('attachment_files', file)
                }
            }
            return this.http.patch<Post>(`${environment.apiUrl}/posts/${postId}/`, formData, {params: parameters});
        }
    }

    deletePost(postId: number): Observable<void> {
        return this.http.delete<void>(`${environment.apiUrl}/posts/${postId}/`);
    }

    getPostById(id: number): Observable<Post> {
        return this.http.get<Post>(`${environment.apiUrl}/posts/${id}/`);
    }

    likePost(postId: number): Observable<GenericResponse<PostLikeResponse>> {
        return this.http.post<GenericResponse<PostLikeResponse>>(`${environment.apiUrl}/posts/${postId}/likes/`, {});
    }

    unlikePost(postId: number): Observable<GenericResponse<PostLikeResponse>> {
        return this.http.delete<GenericResponse<PostLikeResponse>>(`${environment.apiUrl}/posts/${postId}/likes/`);
    }

    dislikePost(postId: number): Observable<GenericResponse<PostCommentLikeResponse>> {
        return this.http.post<GenericResponse<PostCommentLikeResponse>>(`${environment.apiUrl}/posts/${postId}/dislikes/`, {});
    }

    unDislikePost(postId: number,): Observable<GenericResponse<PostCommentLikeResponse>> {
        return this.http.delete<GenericResponse<PostCommentLikeResponse>>(`${environment.apiUrl}/posts/${postId}/dislikes/`);
    }

    getPostComments(postId: number): Observable<PostComment[]> {
        const sort = this.localStorageService.getItem<CustomButtonToggleOption | null>('commentsSortOption', null);
        const params = new HttpParams().set('order_by', sort?.value === 'new' ? 'newest-oldest' : 'oldest-newest');
        return fetchAllPages<PostComment>(this.http, `${environment.apiUrl}/posts/${postId}/comments/`, 1000, params);
    }

    getAllComments(page: number, limit: number, community?: number, user?: number): Observable<any> {
        let parameters: HttpParams = new HttpParams();


        if (community) {
            parameters = parameters.set('community', community);
        }
        if (user) {
            parameters = parameters.set('user', user);
        }
        if (limit) {
            parameters = parameters.set('page_size', limit);
        }
        if (page) {
            parameters = parameters.set('page', page);
        }
        return this.http.get<any>(`${environment.apiUrl}/comments/`, {params: parameters});
    }

    // getPostB(id: number): Observable<Post> {
    //     return this.http.get<Post>(`${environment.apiUrl}/posts/${id}/`);
    // }

    newPostComments(postId: number, type: PostAttachmentType | 'text', comment: PostNewComment): Observable<GenericResponse<PostComment>> {
        let formData: FormData;

        if (type === 'text' || type === 'video_url') {
            return this.http.post<GenericResponse<PostComment>>(`${environment.apiUrl}/posts/${postId}/comments/`, comment);
        } else {
            formData = jsonToFormData(comment);
            return this.http.post<GenericResponse<PostComment>>(`${environment.apiUrl}/posts/${postId}/comments/`, formData);
        }
    }

    deletePostComments(postId: number, commentId: number): Observable<void> {
        return this.http.delete<void>(`${environment.apiUrl}/posts/${postId}/comments/${commentId}/`);
    }

    reportPostComments(postId: number, commentId: number): Observable<void> {
        return this.http.get<void>(`${environment.apiUrl}/posts/${postId}/comments/${commentId}/flag-for-review/`);
    }

    likeComment(postId: number, commentId: number): Observable<GenericResponse<PostCommentLikeResponse>> {
        return this.http.post<GenericResponse<PostCommentLikeResponse>>(`${environment.apiUrl}/posts/${postId}/comments/${commentId}/likes/`, {});
    }

    unlikeComment(postId: number, commentId: number): Observable<GenericResponse<PostCommentLikeResponse>> {
        return this.http.delete<GenericResponse<PostCommentLikeResponse>>(`${environment.apiUrl}/posts/${postId}/comments/${commentId}/likes/`);
    }

    dislikeComment(postId: number, commentId: number): Observable<GenericResponse<PostCommentLikeResponse>> {
        return this.http.post<GenericResponse<PostCommentLikeResponse>>(`${environment.apiUrl}/posts/${postId}/comments/${commentId}/dislikes/`, {});
    }

    unDislikeComment(postId: number, commentId: number): Observable<GenericResponse<PostCommentLikeResponse>> {
        return this.http.delete<GenericResponse<PostCommentLikeResponse>>(`${environment.apiUrl}/posts/${postId}/comments/${commentId}/dislikes/`);
    }

    patchComment(postId: number, commentId: number, body: Partial<PostComment>, type: PostAttachmentType | 'text' = 'text'): Observable<GenericResponse<PostCommentLikeResponse>> {
        let formData: FormData;

        if (type === 'text' || type === 'video_url') {
            return this.http.patch<GenericResponse<PostCommentLikeResponse>>(`${environment.apiUrl}/posts/${postId}/comments/${commentId}/`, body);
        } else {
            formData = jsonToFormData(body);
            return this.http.patch<GenericResponse<PostCommentLikeResponse>>(`${environment.apiUrl}/posts/${postId}/comments/${commentId}/`, formData);
        }
    }

    downloadCSV(url: string, filename: string = 'downloaded_file.csv') {
        this.http.get(url, {responseType: 'blob'}).subscribe((blob) => {
            // Create a new Blob object and download link
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = filename;

            // Trigger download
            document.body.appendChild(link);
            link.click();

            // Clean up
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
        });
    }

    async fetchFile(url: string): Promise<any> {
        let response: Response = await fetch(url, {
            method: 'GET',
            mode: 'cors',
            cache: 'no-store',
        });
        let data: Blob = await response.blob();
        const regex = /([^\/?#]+)\.([a-zA-Z0-9]+)(?=[?#]|$)/;
        const fileName = url?.match(regex)?.[0] || 'image.jpg';
        return new File([data], fileName, {type: data.type});
    }

    reportAction(action: string, model: string, postId?: number | null, communityId?: number | null, metadata?: string | null): Observable<any> {
        let body = {
            "model_name": model,
            "action": action,
            "object_id": postId,
            "community": communityId,
            "metadata": metadata
        }
        return this.http.post(`${environment.apiUrl}/tracked-actions/`, removeNullUndefinedFields(body));
    }

    postSurveyComplete(postId: number) {
        return this.http.post(`${environment.apiUrl}/survey-status/` + postId + '/', {});
    }

    getSurveyStatus(surveyId: number): Observable<any> {
        return this.http.get(`${environment.apiUrl}/survey-status/` + surveyId + '/');
    }

    private customComparator(
        prev: Community | null,
        curr: Community | null
    ): boolean {
        return prev === curr === null || (prev !== null && curr !== null && prev?.id === curr?.id);
    }

}

